import {
  capitalizeTerm,
  type ForgeBoxStatusValue,
  formatAmountWithUnit,
  type MyForgeBox,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner.js";
import { RequireAuth } from "../components/RequireAuth.js";
import { useCurrency } from "../lib/currency.js";
import { forgeErrorMessage, useClaimForgeEarnings, useSetForgeBoxStatus } from "../lib/forge.js";
import { myForgeQueryOptions } from "../lib/queries.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";

/** GET /v1/me/forge: the shopper's own forge boxes, what they have earned, and the claim. */
export function ForgeMinePage() {
  return (
    <RequireAuth>
      <MyForge />
    </RequireAuth>
  );
}

const STATUS_LABEL: Record<MyForgeBox["status"], string> = {
  active: "Live",
  archived: "Archived",
  draft: "Draft",
};

/** A status change the shopper confirms first: deleting is for good. */
interface PendingChange {
  box: MyForgeBox;
  status: ForgeBoxStatusValue;
}

function MyForge() {
  const { sessionToken } = useShopper();
  const terms = useTerminology();
  const { box } = terms;
  const currency = useCurrency();
  const query = useQuery({ ...myForgeQueryOptions(), enabled: !!sessionToken });
  const claim = useClaimForgeEarnings();
  const setStatus = useSetForgeBoxStatus();
  const [confirm, setConfirm] = useState<PendingChange | null>(null);

  const earnings = query.data?.earningsCents ?? 0;
  const boxes = query.data?.boxes ?? [];

  function applyChange(change: PendingChange) {
    setStatus.mutate(
      { boxId: change.box.id, status: change.status },
      {
        onSuccess: () => {
          setConfirm(null);
          toast.success(
            change.status === "deleted"
              ? `${capitalizeTerm(box.singular)} deleted.`
              : change.status === "archived"
                ? `${capitalizeTerm(box.singular)} archived.`
                : `${capitalizeTerm(box.singular)} is live again.`,
          );
        },
        onError: (err) => toast.error(forgeErrorMessage(err, { terms, currency })),
      },
    );
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">My {box.plural}</h1>
          <p className="text-sm text-muted-foreground">
            What you have forged, and what opens of them have earned you.
          </p>
        </div>
        <Link to="/forge/create" className={buttonVariants()}>
          Create {box.singular}
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Unclaimed earnings</CardTitle>
          <CardDescription>
            Your cut of every open, added to your balance when you claim it.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          {query.isPending ? (
            <Skeleton className="h-9 w-32" />
          ) : (
            <span className="text-3xl font-semibold tracking-tight">
              {formatAmountWithUnit(earnings, currency)}
            </span>
          )}
          <Button
            disabled={earnings <= 0 || claim.isPending}
            onClick={() =>
              claim.mutate(undefined, {
                onSuccess: (res) =>
                  toast.success(`Claimed ${formatAmountWithUnit(res.amountCents, currency)}.`),
                onError: (err) => toast.error(forgeErrorMessage(err, { terms, currency })),
              })
            }
          >
            {claim.isPending ? "Claiming…" : "Claim"}
          </Button>
        </CardContent>
      </Card>

      {query.isError && (
        <ErrorBanner>
          {query.error instanceof Error ? query.error.message : `failed to load your ${box.plural}`}
        </ErrorBanner>
      )}

      {query.isPending ? (
        <div className="grid gap-3">
          {["a", "b", "c"].map((id) => (
            <Skeleton key={id} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : boxes.length === 0 && !query.isError ? (
        <p className="text-sm text-muted-foreground">You have not forged any {box.plural} yet.</p>
      ) : (
        <div className="grid gap-3">
          {boxes.map((mine) => (
            <div
              key={mine.id}
              className={`grid grid-cols-[4rem_minmax(0,1fr)] items-center gap-3 rounded-xl border border-border p-3 sm:grid-cols-[4rem_minmax(0,1fr)_auto] ${
                mine.status === "active" ? "" : "opacity-75"
              }`}
            >
              <BoxImage src={mine.imageUrl} alt="" className="size-16 rounded-lg object-cover" />
              <div className="grid min-w-0 gap-1">
                <div className="flex items-center gap-2">
                  {mine.status === "active" ? (
                    <Link
                      to="/boxes/$boxId"
                      params={{ boxId: mine.id }}
                      className="truncate font-medium hover:underline"
                    >
                      {mine.name}
                    </Link>
                  ) : (
                    <span className="truncate font-medium">{mine.name}</span>
                  )}
                  <Badge variant={mine.status === "active" ? "default" : "outline"}>
                    {STATUS_LABEL[mine.status] ?? mine.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>Price {formatAmountWithUnit(mine.priceCents, currency)}</span>
                  <span>
                    {mine.openedCount.toLocaleString()} {mine.openedCount === 1 ? "open" : "opens"}
                  </span>
                  <span>Cut {mine.creatorCutPercent}%</span>
                  <span>Earned {formatAmountWithUnit(mine.lifetimeEarningsCents, currency)}</span>
                </div>
              </div>
              <div className="col-span-2 flex flex-wrap justify-end gap-1.5 sm:col-span-1">
                {mine.status === "active" && (
                  <Link
                    to="/forge/$boxId/edit"
                    params={{ boxId: mine.id }}
                    className={buttonVariants({ variant: "secondary", size: "sm" })}
                  >
                    Edit
                  </Link>
                )}
                {mine.status === "active" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirm({ box: mine, status: "archived" })}
                  >
                    Archive
                  </Button>
                ) : mine.status === "archived" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={setStatus.isPending}
                    onClick={() => applyChange({ box: mine, status: "active" })}
                  >
                    Unarchive
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => setConfirm({ box: mine, status: "deleted" })}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirm?.status === "deleted"
                ? `Delete ${confirm.box.name}?`
                : `Archive ${confirm?.box.name ?? ""}?`}
            </DialogTitle>
            <DialogDescription>
              {confirm?.status === "deleted"
                ? `Nobody can open it again, and it cannot be undone. What it has already earned you stays yours to claim.`
                : `It leaves the ${terms.forge.singular} and nobody can open it until you unarchive it.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button
              variant={confirm?.status === "deleted" ? "destructive" : "default"}
              disabled={setStatus.isPending}
              onClick={() => confirm && applyChange(confirm)}
            >
              {setStatus.isPending
                ? "Saving…"
                : confirm?.status === "deleted"
                  ? "Delete"
                  : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
